import { Fragment, useEffect, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
    updateWalletConfigAction,
    updateWalletConfigStreamAction
} from "../../redux/actions/currenciesActions";
import Calendar from "../../utils/calendar/Calendar";
import { useOutsideClick } from "../../utils/common-hooks";
import { connectionClass } from "../../websocket/socket";
import FieldSetComponent from "../common-components/Fieldset";
import HrFillDataInput from "../common-components/HrFillDataInput";
import SelectInput from "../common-components/SelectInput";

const WalletConfig = ({ setIsWalletEditable, isWalletEditable }) => {
    const nullishValues = [undefined, null, "", false, "undefined"];

    const { currenciesList, currentSide, currentCurrencyId, spinnerStatus, isDarkMode } =
        useSelector(
            (state) => state.persistedReducer,
            (oldValue, newValue) => {
                const {
                    approach: oldSelectedApproach,
                    tradeMethod: oldTradeMethod,
                    operations: { isModelOn: oldIsModelOn },
                    startCandleDate: oldStartCandleDate
                } = oldValue.currenciesList[oldValue.currentCurrencyId];
                const {
                    walletConfig: {
                        quantity: oldQuantity,
                        marginType: oldMarginType,
                        takeProfit: oldTakeProfit,
                        tpControl: oldtpControl,
                        stopLoss: oldStopLoss,
                        slControl: oldslControl,
                        leverage: oldLeverage,
                        maxStopLossLimit: oldMaxStopLossLimit,
                        maxResistanceLevelPrice: oldMaxResistanceLevelPrice,
                        minSupportLevelPrice: oldMinSupportLevelPrice,
                        priceLengthAverage: oldPriceLengthAverage,
                        renkoSkips: oldRenkoSkips,
                        compoundT1: oldCompoundT1,
                        compoundT2: oldCompoundT2,
                        interestId: oldInterestId
                    }
                } = oldValue.currenciesList[oldValue.currentCurrencyId][oldValue.currentSide.side];
                const {
                    operations: { isBinanceOn: isBinanceOnOld }
                } = oldValue.currenciesList[oldValue.currentCurrencyId];
                const {
                    operations: { isBinanceOn: isBinanceOnNew }
                } = newValue.currenciesList[oldValue.currentCurrencyId];

                const {
                    approach: newSelectedApproach,
                    tradeMethod: newTradeMethod,
                    operations: { isModelOn: newIsModelOn },
                    startCandleDate: newStartCandleDate
                } = newValue.currenciesList[newValue.currentCurrencyId];
                const {
                    walletConfig: {
                        quantity: newQuantity,
                        marginType: newMarginType,
                        takeProfit: newTakeProfit,
                        tpControl: newtpControl,
                        stopLoss: newStopLoss,
                        slControl: newslControl,
                        leverage: newLeverage,
                        maxStopLossLimit: newMaxStopLossLimit,
                        maxResistanceLevelPrice: newMaxResistanceLevelPrice,
                        minSupportLevelPrice: newMinSupportLevelPrice,
                        priceLengthAverage: newPriceLengthAverage,
                        renkoSkips: newRenkoSkips,
                        compoundT1: newCompoundT1,
                        compoundT2: newCompoundT2,
                        interestId: newInterestId
                    }
                } = newValue.currenciesList[newValue.currentCurrencyId][newValue.currentSide.side];
                if (
                    oldValue.isDarkMode !== newValue.isDarkMode ||
                    oldSelectedApproach?.approachId !== newSelectedApproach?.approachId ||
                    oldTradeMethod?.methodId !== newTradeMethod.methodId ||
                    oldIsModelOn !== newIsModelOn ||
                    oldValue.currentCurrencyId !== newValue.currentCurrencyId ||
                    oldValue.currentSide.side !== newValue.currentSide.side ||
                    oldMarginType !== newMarginType ||
                    oldTakeProfit !== newTakeProfit ||
                    oldtpControl !== newtpControl ||
                    oldStopLoss !== newStopLoss ||
                    oldslControl !== newslControl ||
                    oldLeverage !== newLeverage ||
                    oldCompoundT1 !== newCompoundT1 ||
                    oldCompoundT2 !== newCompoundT2 ||
                    oldInterestId !== newInterestId ||
                    newStartCandleDate !== oldStartCandleDate ||
                    oldMaxStopLossLimit !== newMaxStopLossLimit ||
                    oldMaxResistanceLevelPrice !== newMaxResistanceLevelPrice ||
                    oldMinSupportLevelPrice !== newMinSupportLevelPrice ||
                    oldPriceLengthAverage !== newPriceLengthAverage ||
                    oldRenkoSkips !== newRenkoSkips ||
                    isBinanceOnOld !== isBinanceOnNew ||
                    oldQuantity !== newQuantity ||
                    oldValue.spinnerStatus !== newValue.spinnerStatus
                ) {
                    return false;
                }
                return true;
            }
        );

    const {
        walletConfig: {
            quantity,
            marginType,
            takeProfit,
            tpControl,
            stopLoss,
            slControl,
            leverage,
            maxStopLossLimit,
            maxResistanceLevelPrice,
            minSupportLevelPrice,
            priceLengthAverage,
            renkoStartDate,
            renkoSkips,
            compoundT1,
            compoundT2
        }
    } = currenciesList[currentCurrencyId][currentSide.side];
    const {
        approach: { approachId },
        interestType: { interestId },
        operations: { isModelOn },
        startCandleDate = new Date(),
        operations: { isBinanceOn }
    } = currenciesList[currentCurrencyId];
    const [calendarData, setCalendarDate] = useState({
        dbDate: !nullishValues.includes(renkoStartDate)
            ? moment(renkoStartDate).format()
            : moment().format(),
        placeholder: !nullishValues.includes(renkoStartDate)
            ? moment(renkoStartDate).format("MM/DD/YYYY")
            : "Start Date",
        value: !nullishValues.includes(renkoStartDate) ? moment(renkoStartDate) : moment(),
        showCalender: false
    });
    const calendarRef = useOutsideClick(() =>
        setCalendarDate((prev) => {
            return {
                ...prev,
                showCalender: false
            };
        })
    );
    const [walletConfigData, setWalletConfigData] = useState({
        quantity,
        leverage,
        takeProfit,
        tpControl,
        stopLoss,
        slControl,
        marginType,
        maxStopLossLimit,
        maxResistanceLevelPrice,
        minSupportLevelPrice,
        priceLengthAverage,
        renkoSkips,
        compoundT1,
        compoundT2
    });

    const dispatch = useDispatch();
    useEffect(() => {
        setWalletConfigData({
            quantity,
            leverage,
            takeProfit,
            tpControl,
            stopLoss,
            slControl,
            marginType,
            maxStopLossLimit,
            maxResistanceLevelPrice,
            minSupportLevelPrice,
            priceLengthAverage,
            renkoSkips,
            compoundT1,
            compoundT2
        });
        setCalendarDate((prev) => {
            return {
                ...prev,
                dbDate: !nullishValues.includes(renkoStartDate)
                    ? moment(renkoStartDate).format()
                    : moment().format(),
                placeholder: !nullishValues.includes(renkoStartDate)
                    ? moment(renkoStartDate).format("MM/DD/YYYY")
                    : "Start Date",
                value: !nullishValues.includes(renkoStartDate) ? moment(renkoStartDate) : moment()
            };
        });
    }, [currentSide.side, currentCurrencyId, approachId, spinnerStatus, isWalletEditable]);
    useEffect(() => {
        connectionClass.socketInfo.off(`configs-${currentCurrencyId}`);
        if (isBinanceOn) {
            connectionClass.socketInfo.on(`configs-${currentCurrencyId}`, (configs) => {
                dispatch(updateWalletConfigStreamAction(configs));
            });
        }
    }, [currentCurrencyId, isBinanceOn]);
    const configList = [
        {
            showInput: true,
            inputType: "Number",
            placeholder: "Currency Volume($)",
            extraUtils: null,
            className: "",
            isDisabled: !isWalletEditable,
            inputValue: walletConfigData.quantity,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        quantity: newValue
                    };
                })
        },
        {
            showInput: true,
            inputType: "Number",
            placeholder: "Leverage",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.leverage,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        leverage: newValue
                    };
                })
        },
        {
            showInput: approachId != 1 ? true : false,
            inputType: "Number",
            placeholder: "Take Profit",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.takeProfit,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        takeProfit: newValue
                    };
                })
        },
        {
            showInput: approachId === 3 || approachId === 4 || approachId === 5 ? true : false,
            inputType: "Number",
            placeholder: "TP Control",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.tpControl,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        tpControl: newValue
                    };
                })
        },
        {
            showInput: approachId != 1 ? true : false,
            inputType: "Number",
            placeholder: "Stop Loss",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.stopLoss,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        stopLoss: newValue
                    };
                })
        },
        {
            showInput: approachId === 3 || approachId === 4 || approachId === 5 ? true : false,
            inputType: "Number",
            placeholder: "SL Control",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.slControl,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        slControl: newValue
                    };
                })
        },
        {
            showInput: approachId === 3 || approachId === 4 || approachId === 5 ? true : false,
            inputType: "Number",
            placeholder: "Profit Distance",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.maxStopLossLimit,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        maxStopLossLimit: newValue
                    };
                })
        },
        {
            showInput: approachId === 3 || approachId === 4 || approachId === 5 ? true : false,
            inputType: "Number",
            placeholder: "FABS Moving",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.maxResistanceLevelPrice,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        maxResistanceLevelPrice: newValue
                    };
                })
        },
        {
            showInput: approachId === 3 || approachId === 4 || approachId === 5 ? true : false,
            inputType: "Number",
            placeholder: "FABS Wave",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.minSupportLevelPrice,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        minSupportLevelPrice: newValue
                    };
                })
        },

        {
            showInput: approachId === 3 || approachId === 4 || approachId === 5 ? true : false,
            inputType: "Number",
            placeholder: "Pretest Check",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.priceLengthAverage,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        priceLengthAverage: newValue
                    };
                })
        },
        {
            showInput: approachId === 3 || approachId === 4 || approachId === 5 ? true : false,
            inputType: "Number",
            placeholder: "Renko Skips",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.renkoSkips,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        renkoSkips: newValue
                    };
                })
        },
        {
            showInput: interestId === 1 ? true : false,
            inputType: "Number",
            placeholder: "Compound T1",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.compoundT1,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        compoundT1: newValue
                    };
                })
        },
        {
            showInput: interestId === 1 ? true : false,
            inputType: "Number",
            placeholder: "Compound T2",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.compoundT2,
            getValueFunc: (newValue) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        compoundT2: newValue
                    };
                })
        },
        {
            showInput: true,
            inputType: "select",
            placeholder: "Margin Type",
            extraUtils: null,
            className: "",
            isDisabled:
                !isWalletEditable && isModelOn
                    ? true
                    : !isModelOn && isWalletEditable
                    ? false
                    : true,
            inputValue: walletConfigData.marginType,
            getValueFunc: (value) =>
                setWalletConfigData((prev) => {
                    return {
                        ...prev,
                        marginType: value.option
                    };
                })
        }
    ];
    const legend = (
        <div className="flex items-center z-90 text-sm">
            <div>Wallet Configuration</div>
            <div className="flex">
                <img
                    onClick={() => setIsWalletEditable(false)}
                    className={`${
                        isWalletEditable ? "block" : "hidden"
                    } max-w-4 ml-1 mt-1 max-h-4 cursor-pointer rounded-full ${
                        isDarkMode ? "border-1" : ""
                    }`}
                    src="/icons/close.png"
                    alt=""
                />
                <img
                    onClick={() => {
                        dispatch(
                            updateWalletConfigAction({
                                ...walletConfigData,
                                renkoStartDate: calendarData.dbDate
                            })
                        );
                        setIsWalletEditable(false);
                    }}
                    className={`max-w-4 ml-1 mt-1 max-h-4 cursor-pointer ${
                        isDarkMode ? "border-1" : ""
                    } ${isWalletEditable ? "block" : "hidden"} rounded-full`}
                    src="/icons/checkmark.png"
                    alt=""
                />
            </div>{" "}
            <img
                onClick={() => setIsWalletEditable(true)}
                className={`max-w-4 max-h-4 ml-1 cursor-pointer mt-1 z-100 ${
                    isDarkMode ? "border-1" : ""
                } ${isWalletEditable ? "hidden" : "block"}`}
                src={`/icons/edit.png`}
            />
        </div>
    );
    let FieldSet = [
        {
            className: "WalletConfig_fieldset",
            legend,
            columnSpan: "1/3",
            rowSpan: "1/2",
            FillDataInput: configList,
            classNames: {
                legendClassName: "block p-1 text-sm ",
                fieldsetClassName:
                    "flex flex-wrap justify-around items-center primaryBorder rounded-sm"
            },
            styles: {
                legendStyle: {},
                fieldsetStyle: {}
            }
        }
    ];
    return (
        <div style={{ zIndex: 1 }}>
            {FieldSet.map((data, index) => {
                const { FillDataInput, legend, classNames, styles } = data;
                return (
                    <FieldSetComponent
                        key={index}
                        legend={legend}
                        classNames={classNames}
                        styles={styles}>
                        {FillDataInput.map((fieldinput, index) => {
                            const {
                                getValueFunc,
                                inputValue,
                                className,
                                extraUtils,
                                inputType,
                                isDisabled,
                                placeholder,
                                showInput
                            } = fieldinput;
                            if (inputType == "Number" && showInput) {
                                return (
                                    <HrFillDataInput
                                        key={index + placeholder}
                                        inputType={"text"}
                                        isDisabled={isDisabled}
                                        className={className}
                                        inputValue={inputValue}
                                        getValueFunc={(e) => getValueFunc(e.target.value)}
                                        placeholder={placeholder}
                                        classNames={{
                                            parentDiv: "flex w-full py-1 px-3 z-10",
                                            placeHolderDiv:
                                                "flex-1 text-vsm 2xl:text-sm flex whitespace-nowrap flex items-center",
                                            input: ` ${
                                                isDarkMode
                                                    ? "bg-secondaryDark"
                                                    : "bg-secondaryLight"
                                            } ${
                                                isDisabled ? "opacity-50" : "opacity-100"
                                            } flex-1 flex text-sm min-h-7 justify-center outline-none text-center items-center text-center max-w-15 md:max-w-18  primaryBorder w-5 md:min-h-9 lg:px-0 `
                                        }}
                                    />
                                );
                            }
                            if (inputType == "select" && showInput) {
                                return (
                                    <div className="flex py-1 px-3 w-full  z-10 ">
                                        <div className="flex-1 text-vsm 2xl:text-sm  flex items-center">
                                            Margin
                                        </div>
                                        <SelectInput
                                            selectFunction={(value) => getValueFunc(value)}
                                            selectHolder={{
                                                styles: {},
                                                className: ` max-w-15  md:max-w-18 flex-end flex-1 flex min-h-7  z-50 ${
                                                    isDisabled ? "opacity-50" : "opacity-100"
                                                }`
                                            }}
                                            selectOptions={{
                                                holderStyle: {},
                                                className:
                                                    "flex text-sm justify-center max-w-25 relative z-50 flex-1 ",
                                                selectedValue: {
                                                    defaultText: walletConfigData.marginType,
                                                    style: { fontSize: 12 },
                                                    className: `${
                                                        isDarkMode
                                                            ? "bg-secondaryDark"
                                                            : "bg-secondaryLight"
                                                    } text-sm flex-1 min-h-7 primaryBorder flex justify-center items-center cursor-pointer text-vsm md:min-h-9`
                                                },
                                                list: {
                                                    holderStyle: {},
                                                    className: `absolute top-8 w-full z-90 mt-2`,
                                                    options: [
                                                        {
                                                            optionId: 1,
                                                            option: "CROSSED"
                                                        },
                                                        {
                                                            optionId: 2,
                                                            option: "ISOLATED"
                                                        }
                                                    ],
                                                    singleOption: {
                                                        divStyle: {
                                                            zIndex: 100
                                                        },
                                                        divClassName: `${
                                                            isDarkMode
                                                                ? "bg-secondaryDark border-1"
                                                                : "bg-tertiaryLight"
                                                        } text-sm z-50 min-h-7 mt-1 primaryBoxShadow flex items-center justify-center cursor-pointer md:min-h-9 max-w-15 md:max-w-18`,
                                                        labelStyle: {
                                                            cursor: "pointer",
                                                            fontSize: 10
                                                        },
                                                        labelClassName: "",
                                                        inputStyle: {
                                                            display: "none"
                                                        }
                                                    }
                                                }
                                            }}
                                            optionName="option"
                                            isEditable={!isDisabled}
                                        />
                                    </div>
                                );
                            }
                        })}

                        <div
                            ref={calendarRef}
                            className={`h-8 mt-2 mb-3  flex py-1 px-3 w-full justify-center items-center   relative z-1 `}>
                            <div className="flex-1 text-vsm 2xl:text-sm  flex items-center ">
                                Renko Start Date
                            </div>
                            <div
                                className={`flex-1 max-w-15  md:max-w-18 ${
                                    isModelOn || !isWalletEditable ? "opacity-50" : "opacity-100"
                                }   ${
                                    isDarkMode ? "bg-secondaryDark" : "bg-secondaryLight"
                                } text-sm flex-1 min-h-7 primaryBorder flex justify-center items-center cursor-pointer text-vsm md:min-h-9`}
                                onClick={() =>
                                    !isModelOn &&
                                    isWalletEditable &&
                                    setCalendarDate((prev) => {
                                        return {
                                            ...prev,
                                            showCalender: !prev.showCalender
                                        };
                                    })
                                }>
                                {calendarData.placeholder}
                            </div>
                            <Calendar
                                key={""}
                                specificDate={startCandleDate}
                                classNames={{
                                    parentDiv: `${
                                        calendarData.showCalender ? "visible" : "invisible"
                                    } shadow-2xl top-10   z-10 min-w-full absolute  text-10   rounded-lg ${
                                        isDarkMode ? "bg-secondaryDark" : "bg-secondaryLight"
                                    }`,
                                    header: {
                                        previous: "flex-1 text-center cursor-pointer",
                                        current: "cursor-pointer flex-5",
                                        next: "cursor-pointer flex-1 text-center ml-1 ",
                                        headerParent: `  text-center h-10 font-black flex items-center  `
                                    },
                                    dayNames: {
                                        holder: `flex bg-black flex-wrap mr-auto items-center  underline`,
                                        dayName: `cursor-pointer flex bg-black font-bold   flex-1 items-center justify-center items-center   text-sm`
                                    },

                                    weekParent: `week my-2 flex flex-wrap mr-auto items-center justify-center`,
                                    dayHolder: ` day rounded-full  ${
                                        isDarkMode
                                            ? "hover:bg-tertiaryDark hover:text-[#000]"
                                            : "hover:bg-white hover:text-black flex justify-center"
                                    }`,
                                    singleDay: " cursor-pointer w-full flex justify-center"
                                }}
                                changeOptions={{
                                    classNames: {
                                        parentDiv:
                                            "w-full min-h-5  flex flex-row-reverse  justify-around py-2 text-[#fff]",
                                        positive: "bg-primaryGreen py-2 min-w-1/4 rounded-lg",
                                        negative: "bg-primaryRed py-2 min-w-1/4 rounded-lg"
                                    },
                                    btnsText: {
                                        positive: "Save",
                                        negative: "Cancel"
                                    },
                                    btnFuncions: {
                                        positive: (day) =>
                                            setCalendarDate((prev) => {
                                                return {
                                                    ...prev,
                                                    dbDate: day.format(),
                                                    value: day,
                                                    placeholder: day.format("MM/DD/YYYY"),
                                                    showCalender: false
                                                };
                                            }),
                                        negative: () =>
                                            setCalendarDate((prev) => {
                                                return {
                                                    ...prev,
                                                    showCalender: false
                                                };
                                            })
                                    },
                                    btnStatus: {
                                        positive: false,
                                        negative: false
                                    }
                                }}
                            />
                        </div>
                    </FieldSetComponent>
                );
            })}
        </div>
    );
};

export default WalletConfig;

// ${isDarkMode ? "bg-secondaryDark" : "bg-secondaryLight"}
